import { languageService } from '@unpispas/upp-base';

import { ViewType, ViewObject } from '../base';

import { dataService } from '../../data';
import { Till } from '../objects/till';

import type { AuditView } from './audit';
import type { TillChange } from '../objects/tillchange';
import type { PlaceView } from './place';
import type { QrCodeView } from './qrcode';
import type { RasppiView } from './rasppi';

/****************************/
/* CLASS VIEW               */
/****************************/

export class _TillView extends ViewObject<Till> {

    /**********************************/
    /* VIEWOBJECT IMPLEMENTATION      */
    /**********************************/

    private get till(): Till {
        return this.object;
    }

    constructor(object: Till, data: dataService){
        super(object, data);
    }

    override OnDestroy(): void {
        super.OnDestroy();
    }

    /**********************************/
    /* VIEW MANAGED INFORMATION       */
    /**********************************/

    private _validtables: QrCodeView[] | null = null;
    private _validaudits: AuditView[] | null = null;
    private _validcashchanges: TillChange[] | null = null;
    private _validcardchanges: TillChange[] | null = null;
    private _validtillchanges: TillChange[] | null = null;

    private RefreshView(targets: string[]): void {
        let _dirty = false;

        if (targets.includes('QRCODE')){
            this._validtables = null;
            _dirty = true;
        }

        if (targets.includes('TILL')){
            this._validtables = null;
            _dirty = true;
        }

        if (targets.includes('AUDIT')){
            this._validaudits = null;
            _dirty = true;
        }

        if (targets.includes('TILLCHANGE')){
            this._validcashchanges = null;
            this._validcardchanges = null;
            this._validtillchanges = null;
            _dirty = true;
        }

        if (_dirty){
            this.DoRefreshView();
        }
    }

    /**********************************/
    /* CHILDREN REGISTRATION          */
    /**********************************/

    override doRegister(): void {
        const weakref = new WeakRef(this);

        for (const _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];

        this._subscriptions.push(
            this.till.OnRefresh.subscribe(
                (targets: string[]) => {
                    const self = weakref.deref();
                    if (self){
                        self.RefreshView(targets);
                    }
                },
            ),
        );

        const _place = this.till.place;
        if (_place){
            this._subscriptions.push(
                _place.OnRefresh.subscribe(
                    (targets: string[]) => {
                        const self = weakref.deref();
                        if (self){
                            self.RefreshView(targets);
                        }
                    },
                ),
            );
        }
    }

    /**********************************/
    /* ACTIVE CHILDREN INFORMATION    */
    /**********************************/

    get place(): PlaceView | null {
        return this.alives.get(this.till.place) as PlaceView | null;
    }

    get rasppi(): RasppiView | null {
        return this.alives.get(this.till.rasppi) as RasppiView | null;
    }

    set rasppi(value: RasppiView | null) {
        this.till.rasppi = value?.object ?? null;
    }

    /** Display label for selectors and lists: name, linked service id, or default i18n. */
    get Label(): string {
        if (this.till.name){
            return this.till.name;
        }

        const _lang = this.data.injector.get(languageService);
        if (this.till.service?.id){
            return _lang.tr('@place_till_name_for_service', [this.till.service.id]);
        }

        return _lang.tr('@place_till_name_default');
    }

    /** Active tables assigned to this till as canonical QrCode views. */
    get tables(): QrCodeView[] {
        if (this._validtables === null){
            const _tables: QrCodeView[] = [];

            for (const _qrcode of this.till.qrcodes){
                const _view = this.alives.get(_qrcode) as QrCodeView | null;
                if (_view){
                    _tables.push(_view);
                }
            }

            this._validtables = _tables;
        }

        return this._validtables;
    }

    /** Committed audits for this till, most recent {@link Audit.end} first (from {@link PlaceView.audits}). */
    get audits(): AuditView[] {
        if (!this.place){
            return [];
        }

        if (this._validaudits === null){
            const _audits: AuditView[] = [];
            for (const _audit of this.place.audits){
                if (_audit.IsPending || _audit.till?.object !== this.till){
                    continue;
                }
                _audits.push(_audit);
            }

            this._validaudits = _audits;
        }

        return this._validaudits;
    }

    /** Open-period draft (`PR`) for this till, if any. */
    get pending(): AuditView | null {
        if (!this.place){
            return null;
        }

        for (const _audit of this.place.audits){
            if (!_audit.IsPending){
                continue;
            }

            if (_audit.till?.object !== this.till){
                continue;
            }

            return _audit;
        }

        return null;
    }

    /** Pending cash in/out for this till while the period is open (`audit` unset, `type === 'CASH'`). */
    get cashchanges(): TillChange[] {
        if (this._validcashchanges === null){
            const _changes: TillChange[] = [];
            for (const _change of this.till.tillchanges ?? []){
                if (_change.audit != null){
                    continue;
                }

                if (_change.type != null && _change.type !== 'CASH'){
                    continue;
                }

                _changes.push(_change);
            }
            this._validcashchanges = _changes;
        }

        return this._validcashchanges;
    }

    /** Pending card adjustments for this till while the period is open (`audit` unset, `type === 'CARD'`). */
    get cardchanges(): TillChange[] {
        if (this._validcardchanges === null){
            const _changes: TillChange[] = [];
            for (const _change of this.till.tillchanges ?? []){
                if (_change.audit != null){
                    continue;
                }

                if (_change.type !== 'CARD'){
                    continue;
                }

                _changes.push(_change);
            }
            this._validcardchanges = _changes;
        }

        return this._validcardchanges;
    }

    /** All pending till changes for this till while the period is open (`audit` unset, any `type`). */
    get tillchanges(): TillChange[] {
        if (this._validtillchanges === null){
            const _changes: TillChange[] = [];
            for (const _change of this.till.tillchanges ?? []){
                if (_change.audit != null){
                    continue;
                }
                _changes.push(_change);
            }
            this._validtillchanges = _changes;
        }

        return this._validtillchanges;
    }
}

/****************************/
/* PROXY VIEW               */
/****************************/

export type TillView = ViewType<_TillView, Till>;

export function TillViewProxy(object: Till, data: dataService): TillView {
    return new _TillView(object, data).Proxy as TillView;
}
